import React, { useState, useEffect } from 'react';
import { ProgressCircular } from 'components';
import InputPassword from 'components/InputPassword';
import { Button, Paper, TextField, Typography } from 'components/mui';
import { Link } from '@mui/material';
import { postLogin } from 'api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotificationProvider } from 'hooks';
import {
	NOTIF_LOGIN_ERROR,
	NOTIF_LOGIN_SUCCESS,
} from 'constants/notificationMsg';
import styles from './Login.module.css';
import logo from '../../assets/images/sds-logo.png';
import caltacLogo from '../../assets/images/caltac-logo.png';
import { useSelector } from 'react-redux';
import { selectUserInfo, storeUserInfo } from 'redux/userInfo/userInfoSlice';
import { UserData } from 'types/userInfoTypes';
import getUserInfoApi from 'api/login/getUserInfoApi';
import convertUserInfo from 'libs/userInfo/convertUserInfo';
import { useDispatch } from 'react-redux';
import AppShowcase from './AppShowcase';

interface LoginData {
	Email: string;
	Password: string;
}

const Login: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { userInfo }: { userInfo: UserData | null } =
		useSelector(selectUserInfo);
	const dispatch = useDispatch();

	const [loginData, setLoginData] = useState<LoginData>({
		Email: '',
		Password: '',
	});
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { notifySuccess, notifyError } = useNotificationProvider();

	// Get the redirect route from where the user came
	const from = location.state?.from?.pathname || '/';

	// If user is already logged in, redirect to homepage or dashboard
	useEffect(() => {
		if (userInfo && userInfo.userId !== '') {
			if (from !== '/') {
				navigate(from, { replace: true });
				return;
			} else {
				if (userInfo.postLoginURL && userInfo.postLoginURL !== '') {
					if (userInfo.postLoginURL.includes('/canappsv3')) {
						const localPostLoginUrl = userInfo.postLoginURL.replace(
							'/canappsv3',
							'',
						); // remove /canappsv3 from the postLoginURL as we are already in canappsv3.
						navigate(localPostLoginUrl, { replace: true });
						return;
					} else {
						// because we are on development mode in canappsv3, we shouldn't redirect if the postLoginURL is outside of the canappsv3.2
						if (process.env.NODE_ENV === 'development') {
							return navigate(from);
						} else {
							const url = new URL(
								userInfo.postLoginURL,
								window.location.origin,
							).href;
							window.location.assign(url);
							return;
						}
					}
				}
			}
			navigate('/', { replace: true });
		}
	}, [userInfo, navigate]);

	/**
	 * Handles input changes for the login form fields.
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 */
	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { id, value } = event.target;
		setLoginData((prev) => ({ ...prev, [id]: value }));
	};

	/**
	 * Handles form submission for login.
	 * @param {React.MouseEvent<HTMLButtonElement>} event
	 */
	const handleLoginSubmit = async (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		event.preventDefault();
		setIsLoading(true);

		try {
			const resultLogin = await postLogin(loginData);
			if (resultLogin.Success) {
				// Fetch user info after login
				const userInfo = await getUserInfoApi();
				const processedData: UserData = await convertUserInfo(userInfo);

				// Update Redux store with user info
				dispatch(storeUserInfo(processedData));

				// Show success notification
				notifySuccess(NOTIF_LOGIN_SUCCESS);

				// useEffect will redirect user when userInfo is updated...
			} else {
				notifyError(NOTIF_LOGIN_ERROR);
			}
		} catch (error) {
			notifyError(
				error instanceof Error ? error.message : 'Login failed',
			);
		} finally {
			setIsLoading(false);
		}
	};

	if (isLoading) return <ProgressCircular />;

	return (
		<div className={styles.page}>
			<div className={styles.showcaseSide}>
				<AppShowcase />
				<svg
					className={styles.waveDivider}
					viewBox="0 0 150 800"
					preserveAspectRatio="none"
					fill="#e8f3fc"
				>
					<path d="M150,0
						L150,800
						L80,800
						Q0,600 80,400
						Q150,200 80,0
						L150,0 Z" />
				</svg>
			</div>
			<div className={styles.loginSide}>
				<div className={styles.container}>
					<Paper className={styles.paper}>
						<main className={styles.main}>
							<Typography variant="h4" className={styles.brand}>
								CANapps 4
							</Typography>
							<Typography variant="h6" className={styles.welcomeText}>
								AI-Powered Software Suite
							</Typography>
							<section className={styles.input_fields}>
								<TextField
									id="Email"
									required
									value={loginData.Email}
									label="Username"
									variant="outlined"
									InputLabelProps={{ shrink: true }}
									onChange={handleInputChange}
								/>
								<InputPassword
									id="Password"
									required
									label="Password"
									value={loginData.Password}
									variant="outlined"
									size="medium"
									onChange={handleInputChange}
								/>
								<Typography
									variant="body2"
									gutterBottom
									style={{ color: 'gray' }}
								>
									Forgot your password?{' '}
									<Link href="/forgot-password" underline="hover">
										Click here
									</Link>
								</Typography>
							</section>
							<section className={styles.buttons}>
								<Button
									id="submit"
									fullWidth
									variant="outlined"
									onClick={handleLoginSubmit}
								>
									SIGN IN
								</Button>
							</section>
						</main>
						<Typography
							variant="body2"
							gutterBottom
							style={{ color: 'gray' }}
						>
							Powered by
						</Typography>
						<img src={logo} alt="SDS Logo" className={styles.logo2} />
					</Paper>
					<Typography variant="caption" className={styles.loginFooter}>
						© 2026 Stream Data Systems. All rights reserved.
					</Typography>
				</div>
			</div>
		</div>
	);
};

export default Login;
